import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  ColumnData,
  DBColumn,
  IGetBucketPreferencesResponse,
  IGetColumnMappingRequest,
  IGetColumnMappingResponse,
  IGetDBColumnsResponse,
  IGetFilteredMappingRequest,
  IGetFilteredMappingResponse,
  IGetProductBucketPreferencesRequest,
  IGetProductPreferencesResponse,
  IMapAllocatorToAgencyRequest,
  IMapAllocatorToAgencyResponse,
  IMappedLOBsRequest,
  IMappedLOBsResponse,
  IUpdateColumnMappingRequest,
  IUpdateColumnMappingResponse,
  IUploadColumnMappingRequest,
  IUploadColumnMappingResponse,
} from 'redux/types/AllocationMapping.types';
import { IFailedPayload } from 'redux/types/Call.types';

interface ICurrentMapping {
  allocationFileColumn: ColumnData | null;
  yucollectDBColumn: DBColumn | null;
  isMasked?: 'true' | 'false';
}

export interface AllocationMappingState {
  error: IFailedPayload | null;
  loading: boolean;
  message: string;
  status: string;
  mappedLOBs: IMappedLOBsResponse | null;
  uploadedAllocationMapping: IUploadColumnMappingResponse | null;
  allocationMapping: IGetColumnMappingResponse | null;
  products: IGetProductPreferencesResponse | null;
  buckets: IGetBucketPreferencesResponse | null;
  customProductList: IGetProductPreferencesResponse | null;
  customBucketList: IGetBucketPreferencesResponse | null;
  dbColumns: IGetDBColumnsResponse | null;
  currentMapping: ICurrentMapping | null;
}

export const allocationMappingInitialState: AllocationMappingState = {
  error: null,
  loading: false,
  message: '',
  status: '',
  mappedLOBs: null,
  uploadedAllocationMapping: null,
  allocationMapping: null,
  products: null,
  buckets: null,
  customProductList: null,
  customBucketList: null,
  dbColumns: null,
  currentMapping: null,
};

const allocationMappingSlice = createSlice({
  name: 'allocationMapping',
  initialState: allocationMappingInitialState,
  reducers: {
    // Upload column mapping
    uploadColumnMapping: (
      state,
      action: PayloadAction<IUploadColumnMappingRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    uploadColumnMappingSuccess: (
      state,
      action: PayloadAction<IUploadColumnMappingResponse>
    ) => {
      state.loading = false;
      state.uploadedAllocationMapping = action.payload;
    },
    uploadColumnMappingFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Get mapping
    getLOBMapping: (state, action: PayloadAction<IMappedLOBsRequest>) => {
      state.loading = true;
      state.error = null;
    },
    getLOBMappingSuccess: (
      state,
      action: PayloadAction<IMappedLOBsResponse>
    ) => {
      state.loading = false;
      state.mappedLOBs = action.payload;
    },
    getLOBMappingFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Get mapping by id
    getColumnMappingById: (
      state,
      action: PayloadAction<IGetColumnMappingRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    getColumnMappingByIdSuccess: (
      state,
      action: PayloadAction<IGetColumnMappingResponse>
    ) => {
      state.loading = false;
      state.allocationMapping = action.payload;
    },
    getColumnMappingByIdFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },
    resetAllocationMapping: (state) => {
      state.loading = false;
      state.uploadedAllocationMapping = null;
      state.allocationMapping = null;
    },

    // Delete mapping
    deleteMapping: (
      state,
      action: PayloadAction<{ id: string; fileType: string }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    deleteMappingSuccess: (
      state,
      action: PayloadAction<{ message: string }>
    ) => {
      state.loading = false;
      state.message = action.payload.message;
    },
    deleteMappingFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Update column mapping
    updateColumnMapping: (
      state,
      action: PayloadAction<IUpdateColumnMappingRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    updateColumnMappingSuccess: (
      state,
      action: PayloadAction<IUpdateColumnMappingResponse>
    ) => {
      state.loading = false;
      state.message = action.payload.message;
      state.status = action.payload.status;
    },
    updateColumnMappingFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Get allocation mapping by filter
    getFilteredMappings: (
      state,
      action: PayloadAction<IGetFilteredMappingRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    getFilteredMappingsSuccess: (
      state,
      action: PayloadAction<IGetFilteredMappingResponse>
    ) => {
      state.loading = false;
    },
    getFilteredMappingsFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Map allocator to agency
    mapAllocatorToAgency: (
      state,
      action: PayloadAction<IMapAllocatorToAgencyRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    mapAllocatorToAgencySuccess: (
      state,
      action: PayloadAction<IMapAllocatorToAgencyResponse>
    ) => {
      state.loading = false;
      state.message = action.payload.message;
    },
    mapAllocatorToAgencyFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },
    // Get product Preferences
    getProductPreferences: (
      state,
      action: PayloadAction<IGetProductBucketPreferencesRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    getProductPreferencesSuccess: (
      state,
      action: PayloadAction<IGetProductPreferencesResponse>
    ) => {
      state.loading = false;
      state.products = action.payload;
    },
    getProductPreferencesFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },
    // Get bucket Preferences
    getBucketPreferences: (
      state,
      action: PayloadAction<IGetProductBucketPreferencesRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    getBucketPreferencesSuccess: (
      state,
      action: PayloadAction<IGetBucketPreferencesResponse>
    ) => {
      state.loading = false;
      state.buckets = action.payload;
    },
    getBucketPreferencesFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Get bucket Preferences
    getDBColumns: (state) => {
      state.loading = true;
      state.error = null;
    },
    getDBColumnsSuccess: (
      state,
      action: PayloadAction<IGetDBColumnsResponse>
    ) => {
      state.loading = false;
      state.dbColumns = action.payload;
    },
    getDBColumnsFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setCurrentMapping: (state, action: PayloadAction<ICurrentMapping>) => {
      state.currentMapping = { ...state.currentMapping, ...action.payload };
    },
    resetCurrentMapping: (state) => {
      state.currentMapping = null;
    },
    setAllocationMapping: (
      state,
      action: PayloadAction<IGetColumnMappingResponse>
    ) => {
      state.allocationMapping = action.payload;
    },

    // Fetch custom product list given allocator ids
    getCustomProductList: (state, action: PayloadAction<string[]>) => {
      state.loading = true;
      state.error = null;
    },
    getCustomProductListSuccess: (
      state,
      action: PayloadAction<IGetProductPreferencesResponse>
    ) => {
      state.loading = false;
      state.customProductList = action.payload;
    },
    getCustomProductListFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Fetch custom bucket list given allocator ids
    getCustomBucketList: (state, action: PayloadAction<string[]>) => {
      state.loading = true;
      state.error = null;
    },
    getCustomBucketListSuccess: (
      state,
      action: PayloadAction<IGetBucketPreferencesResponse>
    ) => {
      state.loading = false;
      state.customBucketList = action.payload;
    },
    getCustomBucketListFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },
    resetCustomProductBucket: (state) => {
      state.customProductList = null;
      state.customBucketList = null;
    },
  },
});

export const {
  uploadColumnMapping,
  uploadColumnMappingSuccess,
  uploadColumnMappingFailure,
  getLOBMapping,
  getLOBMappingSuccess,
  getLOBMappingFailure,
  getColumnMappingById,
  getColumnMappingByIdSuccess,
  getColumnMappingByIdFailure,
  deleteMapping,
  deleteMappingSuccess,
  deleteMappingFailure,
  updateColumnMapping,
  updateColumnMappingSuccess,
  updateColumnMappingFailure,
  getFilteredMappings,
  getFilteredMappingsSuccess,
  getFilteredMappingsFailure,
  mapAllocatorToAgency,
  mapAllocatorToAgencySuccess,
  mapAllocatorToAgencyFailure,
  getProductPreferences,
  getProductPreferencesSuccess,
  getProductPreferencesFailure,
  getBucketPreferences,
  getBucketPreferencesSuccess,
  getBucketPreferencesFailure,
  getDBColumns,
  getDBColumnsSuccess,
  getDBColumnsFailure,
  setCurrentMapping,
  resetCurrentMapping,
  setAllocationMapping,
  getCustomProductList,
  getCustomProductListSuccess,
  getCustomProductListFailure,
  getCustomBucketList,
  getCustomBucketListSuccess,
  getCustomBucketListFailure,
  resetCustomProductBucket,
  resetAllocationMapping,
} = allocationMappingSlice.actions;

export default allocationMappingSlice.reducer;
